import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { IGetCategoriesItemDto } from '../../../pages/categories.dto';

@Component({
  selector: 'app-global-category-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './global-category-dropdown.component.html',
  styleUrls: ['./global-category-dropdown.component.scss']
})
export class GlobalCategoryDropdownComponent {
  private readonly allCategoryId = 0;
  private readonly noCategoryId = -1;
  private readonly allLabelTermKey = 'Filter default selected item - (general) - All';
  private readonly noCategoryLabelTermKey = 'No category selected label (Global category dropdown) - No category';

  @Input() categories: IGetCategoriesItemDto[] = [];
  @Input() selectedCategoryId: number = 0;
  @Input() label: string = 'Category';
  @Output() categorySelected = new EventEmitter<IGetCategoriesItemDto>();

  public isCategoryTreeOpen: boolean = false;
  public categorySelectedLabel: string = '';
  public resolvedCategories: IGetCategoriesItemDto[] = [];
  public expandedCategoryNodeIds: Set<number> = new Set<number>();
  @ViewChild('categoryTreeDropdownRef') categoryTreeDropdownRef!: ElementRef;

  constructor(
    private elementRef: ElementRef,
    private translocoService: TranslocoService
  ) {}

  ngOnChanges(): void {
    this.resolvedCategories = this.buildDisplayCategories();
    this.syncSelectedLabel();
  }

  private buildDisplayCategories(): IGetCategoriesItemDto[] {
      const containsAll = (this.categories ?? []).some(x => x?.id === null);
      if (containsAll) {
          return [...(this.categories ?? [])];
      }
      return [this.createSimpleOption(null, this.translateAllLabel()), ...(this.categories ?? [])];
  }

  private syncSelectedLabel(): void {
      if (this.selectedCategoryId === this.noCategoryId) {
          this.categorySelectedLabel = this.translateNoCategoryLabel();
          return;
      }

      if (!this.selectedCategoryId || this.selectedCategoryId === this.allCategoryId) {
          this.categorySelectedLabel = this.translateAllLabel();
          return;
      }

      this.updateSelectedLabel(this.resolvedCategories);
  }

  private translateAllLabel(): string {
      return this.translocoService.translate(this.allLabelTermKey);
  }

  private translateNoCategoryLabel(): string {
      return this.translocoService.translate(this.noCategoryLabelTermKey);
  }

  private createSimpleOption(id: number | null, category_name: string): IGetCategoriesItemDto {
      return {
          id,
          category_name,
          updated_at: null,
          parent_category: null,
          sub_category: null,
          created_at: null,
          user_id: null,
          workspace_id: null,
          children: []
      } as IGetCategoriesItemDto;
  }

  private updateSelectedLabel(nodes: IGetCategoriesItemDto[]): boolean {
      for (const node of nodes) {
          if (node.id === this.selectedCategoryId) {
              this.categorySelectedLabel = node.category_name;
              return true;
          }
          if (node.children && node.children.length > 0) {
              const found = this.updateSelectedLabel(node.children);
              if (found) {
                  return true;
              }
          }
      }
      return false;
  }

  toggleCategoryTreeDropdown() {
      this.isCategoryTreeOpen = !this.isCategoryTreeOpen;
  }

  toggleCategoryExpand(event: Event, node: IGetCategoriesItemDto) {
      if (!this.hasCategoryChildren(node) || typeof node.id === 'undefined') {
          return;
      }
      event.stopPropagation();
      
      if (this.expandedCategoryNodeIds.has(node.id)) {
          this.expandedCategoryNodeIds.delete(node.id);
      } else {
          this.expandedCategoryNodeIds.add(node.id);
      }
  }

  isCategoryExpanded(node: IGetCategoriesItemDto): boolean {
      return typeof node.id !== 'undefined' && this.expandedCategoryNodeIds.has(node.id);
  }

  handleCategoryTreeSelect(node: IGetCategoriesItemDto) {
      this.categorySelectedLabel = node.category_name || '';
      this.selectedCategoryId = node?.id == null ? this.allCategoryId : Number(node.id);
      this.isCategoryTreeOpen = false;
      this.categorySelected.emit(node);
  }

  hasCategoryChildren(node: IGetCategoriesItemDto): boolean {
      return !!node.children && node.children.length > 0;
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent) {
      if (this.isCategoryTreeOpen && this.categoryTreeDropdownRef && 
          !this.categoryTreeDropdownRef.nativeElement.contains(event.target)) {
          this.isCategoryTreeOpen = false;
      }
  }
}
